import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../config/apiConfig';
import { FaPlus, FaTrash, FaCheck, FaTimes, FaSpinner, FaUpload, FaImage, FaList } from 'react-icons/fa';

const AuthImageManagement = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    type: 'user_login',
    aspectRatio: '9:16',
    title: '',
    description: '',
    priority: 0,
    image: null
  });

  const types = [
    { value: 'user_login', label: 'User Login' },
    { value: 'corporate_login', label: 'Corporate Login' },
    { value: 'corporate_signup', label: 'Corporate Signup' },
    { value: 'driver_login', label: 'Driver Login' },
    { value: 'driver_register', label: 'Driver Register' },
    { value: 'web_hero', label: 'Web Hero Section' },
    { value: 'driver_page_hero', label: 'Driver Page Hero' },
    { value: 'top_experience', label: 'Top Experience Card' },
    { value: 'route_section_main', label: 'Popular Route - Main Image' },
    { value: 'route_section_small', label: 'Popular Route - Small Float' }
  ];

  const aspectRatios = ["1:1", "2:3", "9:16", "16:9"];

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/auth-images/admin`, {
        params: { type: '', aspectRatio: '' }, // Fetch all initially or handle filtering
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setImages(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching auth images:', error);
      toast.error('Failed to load images');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, image: e.target.files[0] });
  };

  const handleEdit = (img) => {
    setEditingId(img._id);
    setFormData({
      type: img.type,
      aspectRatio: img.aspectRatio,
      title: img.title || '',
      description: img.description || '',
      priority: img.priority || 0,
      image: null // User can optionally upload a new image
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    // Only require image if we're not editing
    if (!editingId && !formData.image) return toast.error('Please select an image');

    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      const data = new FormData();
      if (formData.image) data.append('image', formData.image);
      data.append('type', formData.type);
      data.append('aspectRatio', formData.aspectRatio);
      data.append('title', formData.title);
      data.append('description', formData.description);
      data.append('priority', formData.priority);

      let response;
      if (editingId) {
        response = await axios.put(`${API_URL}/auth-images/${editingId}`, data, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        response = await axios.post(`${API_URL}/auth-images/upload`, data, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      if (response.data.success) {
        toast.success(editingId ? 'Updated successfully' : 'Image uploaded successfully');
        setShowForm(false);
        setEditingId(null);
        setFormData({ ...formData, image: null, title: '', description: '' });
        fetchImages();
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.message || 'Action failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/auth-images/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deleted successfully');
      fetchImages();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/auth-images/${id}`, 
        { isActive: !currentStatus }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchImages();
    } catch (error) {
      toast.error('Status update failed');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Visual Assets Management</h1>
          <p className="text-gray-500">Manage background images for Login, Signup, and Web Hero sections</p>
        </div>
        <button
          onClick={() => {
            if (showForm) {
              setEditingId(null);
              setFormData({ ...formData, image: null, title: '', description: '' });
            }
            setShowForm(!showForm);
          }}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
        >
          {showForm ? <FaTimes /> : <FaPlus />} 
          {showForm ? 'Cancel' : 'Add New Image'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100 mb-8 max-w-2xl">
          <h2 className="text-xl font-bold mb-4 text-blue-600">
            {editingId ? 'Update Existing Image' : 'Add New Image'}
          </h2>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">App Type</label>
                <select 
                  className="w-full border rounded-lg p-2"
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                >
                  {types.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <div className="mt-2 text-[10px] bg-blue-50 p-2 rounded border border-blue-100 text-blue-700">
                  <p className="font-bold mb-1 underline">Recommended Size:</p>
                  {formData.type === 'web_hero' && <p>1920x800px or higher</p>}
                  {formData.type === 'driver_page_hero' && <p>1920x600px or higher</p>}
                  {formData.type === 'top_experience' && <p>800x600px (4:3 ratio)</p>}
                  {formData.type === 'route_section_main' && <p>600x500px</p>}
                  {formData.type === 'route_section_small' && <p>250x200px</p>}
                  {(formData.type.includes('login') || formData.type.includes('signup')) && <p>1080x1920px (9:16 vertical)</p>}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Aspect Ratio</label>
                <select 
                  className="w-full border rounded-lg p-2"
                  value={formData.aspectRatio}
                  onChange={(e) => setFormData({...formData, aspectRatio: e.target.value})}
                >
                  {aspectRatios.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Title (Optional)</label>
              <input 
                type="text" 
                className="w-full border rounded-lg p-2" 
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description (Optional)</label>
              <textarea 
                className="w-full border rounded-lg p-2" 
                rows="2"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              ></textarea>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {editingId ? 'Replace Image (Optional)' : 'Select Image'}
              </label>
              <input 
                type="file" 
                accept="image/*"
                onChange={handleFileChange}
                className="w-full border border-dashed rounded-lg p-4 bg-gray-50"
              />
            </div>
            <button
              type="submit"
              disabled={uploading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition disabled:bg-blue-300 flex items-center justify-center gap-2"
            >
              {uploading ? <FaSpinner className="animate-spin" /> : <FaUpload />}
              {uploading ? 'Processing...' : editingId ? 'Update Image' : 'Upload Image'}
            </button>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center p-20"><FaSpinner className="animate-spin text-4xl text-blue-600" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {images.map(img => (
            <div key={img._id} className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 group">
              <div className={`relative bg-gray-100 overflow-hidden ${img.type === 'user_login' ? 'aspect-[3/4]' : 'aspect-[16/9]'}`}>
                <img src={img.imageUrl} alt={img.title} className="w-full h-full object-cover" />
                <div className="absolute top-2 right-2 flex gap-2">
                  <span className={`px-2 py-1 rounded text-xs font-bold text-white shadow-sm ${img.isActive ? 'bg-green-500' : 'bg-red-500'}`}>
                    {img.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-bold text-gray-800">{img.title || 'Untitled'}</h3>
                    <p className="text-xs text-blue-600 font-bold uppercase mb-1">{img.type.replace('_', ' ')}</p>
                    {img.description && <p className="text-[10px] text-gray-500 line-clamp-2">{img.description}</p>}
                  </div>
                  <p className="text-xs bg-gray-100 px-2 py-1 rounded font-mono font-bold text-gray-600">{img.aspectRatio}</p>
                </div>
                <div className="flex justify-between items-center mt-4">
                  <div className="flex gap-2">
                    <button 
                      onClick={() => toggleStatus(img._id, img.isActive)}
                      className={`text-sm px-3 py-1 rounded transition ${img.isActive ? 'bg-orange-100 text-orange-600 hover:bg-orange-200' : 'bg-green-100 text-green-600 hover:bg-green-200'}`}
                    >
                      {img.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button 
                      onClick={() => handleEdit(img)}
                      className="text-sm px-3 py-1 rounded bg-blue-100 text-blue-600 hover:bg-blue-200 transition"
                    >
                      Edit
                    </button>
                  </div>
                  <button 
                    onClick={() => handleDelete(img._id)}
                    className="text-red-600 hover:bg-red-50 p-2 rounded transition"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AuthImageManagement;
